import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, signal, computed } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { Client, CognitoUser, CognitoUserPool, User, Section, Location, Role } from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../services/navigation.service';
import { CognitoService } from '../../../../services/cognito.service';
import { ClientService } from '../../../../services/client.service';
import { UserService } from '../../../../services/user.service';
import { LanguageService } from '../../../../services/language.service';
import { CognitoGroupSelectorComponent } from '../../../ui/admin/cognito-group-selector/cognito-group-selector.component';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { AdminService } from '../../../../services/admin.service';
import { SectionService } from '../../../../services/section.service';
import { LocationService } from '../../../../services/location.service';
import { LocationsSelectorComponent } from '../../../ui/admin/locations-selector/locations-selector.component';
import { SectionsSelectorComponent } from '../../../ui/admin/sections-selector/sections-selector.component';
import { ClientsSelector } from '../../../ui/admin/clients-selector/clients-selector';

@Component({
	selector: 'app-cognito-user-form',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		CognitoGroupSelectorComponent,
		LocationsSelectorComponent,
		SectionsSelectorComponent,
		ClientsSelector,
	],
	templateUrl: './cognito-user-form.component.html',
	styleUrl: './cognito-user-form.component.scss',
})
export class CognitoUserFormComponent implements OnInit {
	@Input('cognitoUser') cognitoUser: CognitoUser | undefined;
	@Output('onSubmit') onSubmitEmitter: EventEmitter<any> = new EventEmitter<any>();

	private sub: Subscription | undefined;

	readonly loading = signal(false);
	readonly groups = signal<string[]>([]);
	readonly locations = signal<Location[]>([]);
	readonly selectedSections = signal<Section[]>([]);
	readonly selectedSectionUuids = signal<string[]>([]);
	readonly selectedGroups = signal<string[]>([]);
	readonly selectedLocationUuids = signal<string[]>([]);
	readonly selectedClientUuids = signal<string[]>([]);
	readonly selectedClients = signal<Client[]>([]);

	readonly metabiUser = signal<User | undefined>(undefined);

	readonly Role = Role;

	readonly hasRequiredSelections = computed(
		() =>
			this.selectedClients().length > 0 && this.selectedSections().length > 0 && this.selectedGroups().length > 0
	);

	constructor(
		public nav: NavigationService,
		public cognitoService: CognitoService,
		public clientService: ClientService,
		public userService: UserService,
		public languageService: LanguageService,
		private sectionService: SectionService,
		public adminService: AdminService,
		public locationService: LocationService
	) {}

	ngOnInit() {
		this.sub = this.adminService.selectedUserPool$.subscribe((pool: CognitoUserPool | undefined) => {
			if (!pool) return;
			this.cognitoService.listUserGroups(pool.id).then((groups: string[]) => {
				this.groups.set(groups);
			});
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.cognitoUser) {
			this.reset$();
			return;
		}
		if (changes['cognitoUser'] && changes['cognitoUser'].currentValue !== changes['cognitoUser'].previousValue) {
			this.reset$();
		}
	}

	onClientsChanged(clients: Client[]): void {
		this.selectedClients.set(clients);
		this.selectedClientUuids.set(clients.map(c => c.uuid));

		if (clients.length > 0) {
			this.locations.set([]);
			clients.forEach(client => {
				this.locationService.getByClient$(client.uuid, 0, 100).then(locations => {
					this.locations.update(current => [...current, ...locations]);
				});
			});
		}
	}

	onSectionsChanged(sections: Section[]): void {
		this.selectedSections.set(sections);
		this.selectedSectionUuids.set(sections.map(s => s.uuid));
	}

	onGroupsChanged(groups: string[]): void {
		this.selectedGroups.set(groups);
	}

	onLocationsChanged(locations: Location[]): void {
		this.selectedLocationUuids.set(locations.map(l => l.uuid));
	}

	private async reset$(): Promise<void> {
		this.selectedClients.set([]);
		this.selectedSections.set([]);
		this.selectedGroups.set([]);
		this.selectedLocationUuids.set([]);
		this.metabiUser.set(undefined);
		this.loading.set(true);

		if (!this.cognitoUser?.uuid) {
			console.warn('No cognito user');
			this.loading.set(false);
			return;
		}

		try {
			const user = await this.userService.get$(this.cognitoUser.uuid, true, true, true);
			if (!user) {
				console.warn('No metabi user');
				return;
			}
			this.metabiUser.set(user);

			this.onGroupsChanged(this.cognitoUser?.groups || []);

			// Clients
			if (user.user_client && user.user_client.length > 0) {
				const clients = await firstValueFrom(this.clientService.clients$);
				const selected = clients.filter(c => user.user_client?.some(uc => uc.client_uuid === c.uuid));
				this.onClientsChanged(selected);
			}

			// Sections
			if (user.user_section) {
				const sections = (user.user_section.map(us => us.section) || []) as Section[];
				this.onSectionsChanged(sections);
			}

			// Locations (filtered by currently selected clients)
			if (user.user_location) {
				const matching = user.user_location
					.filter(ul => this.selectedClientUuids().includes(ul.location!.client_uuid))
					.map(ul => ul.location!) as Location[];
				this.onLocationsChanged(matching);
			}
		} catch (error) {
			console.warn('Error loading user data:', error);
		} finally {
			this.loading.set(false);
		}
	}

	async onSubmit$() {
		if (!this.cognitoUser?.uuid) {
			throw new Error('No cognito user');
		}
		if (!this.adminService.selectedUserPool$.value?.id) {
			throw new Error('No user pool selected');
		}

		try {
			this.loading.set(true);

			const payload = {
				cognito_uuid: this.cognitoUser.uuid,
				data_json: {},
				client_uuids: this.selectedClients().map(c => c.uuid),
				locationUuids: this.selectedLocationUuids(),
				active: this.metabiUser()?.active || false,
				account_locked: this.metabiUser()?.account_locked || false,
				active_start_date: this.metabiUser()?.active_start_date,
				active_end_date: this.metabiUser()?.active_end_date,
				userPoolId: this.adminService.selectedUserPool$.value?.id,
			};

			const userPoolId = this.adminService.selectedUserPool$.value?.id;

			await this.userService.upsert$(this.cognitoUser.uuid, payload);
			await this.sectionService.removeUserFromAllSections$(this.cognitoUser.uuid);

			for (const section of this.selectedSections()) {
				await this.sectionService.addUserToSection$(section.uuid, this.cognitoUser.uuid);
			}

			await this.cognitoService.updateUserGroups$(userPoolId, this.cognitoUser.uuid, this.selectedGroups());
		} catch (error: unknown) {
			alert('Error saving user data: ' + (error as Error).message);
		} finally {
			this.loading.set(false);
		}
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}

	public getFormattedConfirmationStatus(): string {
		const status = this.cognitoUser?.confirmationStatus;

		if (!status) {
			return 'Unknown';
		}

		return status
			.toLowerCase()
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	public isConfirmedUserStatus(): boolean {
		return this.cognitoUser?.confirmationStatus?.toUpperCase() === 'CONFIRMED';
	}
}
